import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { dbFs } from "@/lib/firebase";
import { brl } from "@/lib/format";
import { paraCentavos } from "@/lib/dinheiro";
import { PageHeader, Panel, Th, Td, EmptyState, Campo, Escolha, Erro } from "@/components/parker";
import {
  useEmpresas,
  useCategorias,
  useCentros,
  useLancamentos,
  useOrcamentos,
  useRateios,
  usePorLancamento,
  useIndice,
} from "@/lib/dados";
import { partesDe, type Parte } from "@/lib/atribuicao";
import { baixarCSV, centavosParaCSV } from "@/lib/exportar";
import { Download } from "lucide-react";
import { mensagemDeErro } from "@/lib/fns";
import { useSessao } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { Orcamento as TipoOrcamento } from "@/lib/tipos";

export const Route = createFileRoute("/_authenticated/orcamento")({ component: Orcamento });

const MESES = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

/** Acima disso a linha já acende amarelo, antes de estourar. */
const LIMIAR_ATENCAO = 0.85;

/** Dinheiro que já saiu. */
const REALIZADO = ["pago", "conciliado", "divergente"];
/** Dinheiro que ainda não saiu mas já é compromisso (§11). */
const COMPROMETIDO = ["aguardando_aprovacao", "aprovado"];

function Orcamento() {
  const { isAdmin, usuario } = useSessao();
  const ano = new Date().getFullYear();

  const empresas = useEmpresas();
  const categorias = useCategorias();
  const centros = useCentros();
  const lancamentos = useLancamentos();
  const orcamentos = useOrcamentos(ano);
  const rateios = useRateios();
  const rateioPorLancamento = usePorLancamento(rateios.dados);

  const nomeEmpresa = useIndice(empresas.dados);
  const nomeCategoria = useIndice(categorias.dados);
  const nomeCentro = useIndice(centros.dados);

  const [f, setF] = useState({
    empresaId: "",
    categoriaId: "",
    centroCustoId: "",
    valor: "",
    mes: String(new Date().getMonth() + 1),
  });
  const [salvando, setSalvando] = useState(false);

  if (orcamentos.erro) return <Erro mensagem={orcamentos.erro} />;

  async function definir() {
    if (!f.empresaId || !f.categoriaId) {
      toast.error("Informe empresa e categoria.");
      return;
    }
    setSalvando(true);
    try {
      await addDoc(collection(dbFs, "orcamentos"), {
        empresaId: f.empresaId,
        categoriaId: f.categoriaId,
        centroCustoId: f.centroCustoId || null,
        ano,
        mes: Number(f.mes),
        valorCentavos: paraCentavos(f.valor),
        // O gatilho de auditoria usa estes campos para saber de quem foi a escrita.
        criadoPor: usuario?.uid ?? null,
        criadoPorNome: usuario?.displayName ?? usuario?.email ?? null,
        createdAt: serverTimestamp(),
      });
      setF((s) => ({ ...s, valor: "" }));
      toast.success("Orçamento definido.");
    } catch (e) {
      toast.error(mensagemDeErro(e));
    } finally {
      setSalvando(false);
    }
  }

  // As partes seguem o rateio quando existe: orçamento de uma empresa não pode
  // carregar a fatia que é de outra.
  const CONTAM = [...REALIZADO, ...COMPROMETIDO];
  const partes = partesDe(
    lancamentos.dados.filter((l) => CONTAM.includes(l.status)),
    rateioPorLancamento,
  );

  /** As partes que pertencem a uma linha de orçamento (empresa, categoria, centro e mês). */
  const partesDaLinha = (o: TipoOrcamento): Parte[] =>
    partes.filter((p) => {
      if (p.empresaId !== o.empresaId) return false;
      if (o.categoriaId && p.categoriaId !== o.categoriaId) return false;
      if (o.centroCustoId && p.centroCustoId !== o.centroCustoId) return false;
      const [a, m] = p.dataCompetencia.split("-").map(Number);
      if (a !== o.ano) return false;
      return !o.mes || m === o.mes;
    });

  const somar = (lista: Parte[]) => lista.reduce((s, p) => s + Number(p.valorCentavos), 0);

  const linhas = orcamentos.dados
    .map((o) => {
      const minhas = partesDaLinha(o);
      const real = somar(minhas.filter((p) => REALIZADO.includes(p.status)));
      const comprometido = somar(minhas.filter((p) => COMPROMETIDO.includes(p.status)));
      const previsto = Number(o.valorCentavos);
      const consumido = real + comprometido;
      return {
        o,
        real,
        comprometido,
        consumido,
        previsto,
        pct: previsto > 0 ? consumido / previsto : 0,
      };
    })
    .sort((a, b) => (a.o.mes ?? 0) - (b.o.mes ?? 0));

  function exportar() {
    baixarCSV(
      "orcamento",
      [
        "Ano",
        "Mês",
        "Empresa",
        "Categoria",
        "Centro de custo",
        "Previsto",
        "Comprometido",
        "Realizado",
        "Saldo",
      ],
      linhas.map(({ o, real, comprometido, consumido, previsto }) => [
        o.ano,
        o.mes ?? "ano",
        nomeEmpresa.get(o.empresaId) ?? "",
        o.categoriaId ? (nomeCategoria.get(o.categoriaId) ?? "") : "todas",
        o.centroCustoId ? (nomeCentro.get(o.centroCustoId) ?? "") : "todos",
        centavosParaCSV(previsto),
        centavosParaCSV(comprometido),
        centavosParaCSV(real),
        centavosParaCSV(previsto - consumido),
      ]),
    );
  }

  return (
    <>
      <PageHeader
        title="Orçamento"
        subtitle={`Previsto, comprometido e realizado — exercício ${ano}. Comprometido é o que já foi aprovado mas ainda não saiu do caixa.`}
        actions={
          <Button variant="outline" onClick={exportar} disabled={linhas.length === 0}>
            <Download className="size-4" /> Exportar
          </Button>
        }
      />

      {isAdmin ? (
        <Panel className="mb-4 grid gap-4 p-5 sm:grid-cols-5">
          <Campo label="Empresa">
            <Escolha
              value={f.empresaId}
              onChange={(v) => setF((s) => ({ ...s, empresaId: v }))}
              options={empresas.dados.map((e) => ({ id: e.id, label: e.nome }))}
            />
          </Campo>
          <Campo label="Categoria">
            <Escolha
              value={f.categoriaId}
              onChange={(v) => setF((s) => ({ ...s, categoriaId: v }))}
              options={categorias.dados.map((c) => ({
                id: c.id,
                label: `${c.codigo} · ${c.nome}`,
              }))}
            />
          </Campo>
          <Campo label="Centro de custo">
            <Escolha
              value={f.centroCustoId}
              onChange={(v) => setF((s) => ({ ...s, centroCustoId: v }))}
              permitirVazio
              placeholder="Todos"
              options={centros.dados.map((c) => ({ id: c.id, label: `${c.codigo} · ${c.nome}` }))}
            />
          </Campo>
          <Campo label="Mês">
            <Escolha
              value={f.mes}
              onChange={(v) => setF((s) => ({ ...s, mes: v }))}
              options={MESES.map((m, i) => ({ id: String(i + 1), label: m }))}
            />
          </Campo>
          <Campo label="Valor previsto">
            <div className="flex gap-2">
              <Input
                className="num"
                inputMode="decimal"
                placeholder="0,00"
                value={f.valor}
                onChange={(e) => setF((s) => ({ ...s, valor: e.target.value }))}
              />
              <Button onClick={() => void definir()} disabled={salvando}>
                Definir
              </Button>
            </div>
          </Campo>
        </Panel>
      ) : null}

      <Panel className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr>
              <Th>Empresa</Th>
              <Th>Categoria</Th>
              <Th>Centro de custo</Th>
              <Th>Mês</Th>
              <Th className="text-right">Previsto</Th>
              <Th className="text-right">Comprometido</Th>
              <Th className="text-right">Realizado</Th>
              <Th className="text-right">Saldo</Th>
              <Th>Alerta</Th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(({ o, real, comprometido, consumido, previsto, pct }) => (
              <tr key={o.id}>
                <Td>{nomeEmpresa.get(o.empresaId) ?? "—"}</Td>
                <Td>{o.categoriaId ? (nomeCategoria.get(o.categoriaId) ?? "—") : "todas"}</Td>
                <Td className="text-muted-foreground">
                  {o.centroCustoId ? (nomeCentro.get(o.centroCustoId) ?? "—") : "todos"}
                </Td>
                <Td>{o.mes ? MESES[o.mes - 1] : "ano"}</Td>
                <Td className="num text-right">{brl(previsto)}</Td>
                <Td className="num text-right text-warning">
                  {comprometido ? brl(comprometido) : "—"}
                </Td>
                <Td className="num text-right">{brl(real)}</Td>
                <Td
                  className={`num text-right ${consumido > previsto ? "text-negative" : "text-positive"}`}
                >
                  {brl(previsto - consumido)}
                </Td>
                <Td>
                  {pct > 1 ? (
                    <span className="text-xs text-negative">estourou</span>
                  ) : pct > LIMIAR_ATENCAO ? (
                    <span className="text-xs text-warning">tendência de estouro</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">dentro</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
        {linhas.length === 0 ? (
          <EmptyState
            title="Nenhum orçamento definido"
            hint={isAdmin ? "Defina o previsto por empresa e categoria acima." : undefined}
          />
        ) : null}
      </Panel>
    </>
  );
}
